import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore, logoutUser } from '@/hooks/useAppStore';
import { logout } from '@/api/auth';
import { clearTokens } from '@/api/client';
import { deleteMe } from '@/api/user';
import { changeLanguage, getCurrentLang } from '@/i18n/index';
import { useAppConfig } from '@/hooks/useAppConfig';
import AgreementSheet from '@/components/feature/AgreementSheet';
import { APP_BRAND_MARK_URL } from '@/config/brand';
import AccountSecuritySheet from './AccountSecuritySheet';

type ModalType = 'logout' | 'clearCache' | 'about' | 'accountSecurity' | 'notifications' | 'rateUs' | 'language' | 'deleteAccount' | null;

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, authInitialized } = useAppStore();
  const config = useAppConfig();
  const { t, i18n } = useTranslation();
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [activeAgreement, setActiveAgreement] = useState<string | null>(null);
  const [cacheCleared, setCacheCleared] = useState(false);
  const [cacheSize, setCacheSize] = useState(t('settings_cacheCalculating'));
  const [freedSize, setFreedSize] = useState('');
  const [currentLang, setCurrentLang] = useState<'zh' | 'en'>(getCurrentLang());

  useEffect(() => {
    try {
      const bytes = new Blob([JSON.stringify(localStorage)]).size;
      if (bytes < 1024) setCacheSize(`${bytes} B`);
      else if (bytes < 1024 * 1024) setCacheSize(`${(bytes / 1024).toFixed(1)} KB`);
      else setCacheSize(`${(bytes / 1024 / 1024).toFixed(1)} MB`);
    } catch {
      setCacheSize(t('settings_cacheLessThan1MB'));
    }
  }, [t]);
  const [notifications, setNotifications] = useState({ generateDone: true, activity: true, system: false });
  const [rateScore, setRateScore] = useState(0);
  const [rateSubmitted, setRateSubmitted] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteStep, setDeleteStep] = useState(1);
  const [deleteError, setDeleteError] = useState('');

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // ignore errors — proceed with local logout regardless
    }
    await clearTokens();
    logoutUser();
    setActiveModal(null);
    navigate('/login', { replace: true });
  };

  const handleSwitchLang = (lang: 'zh' | 'en') => {
    changeLanguage(lang);
    setCurrentLang(lang);
    setActiveModal(null);
  };

  const handleClearCache = () => {
    setFreedSize(cacheSize);
    const accessToken = localStorage.getItem('gp_access_token');
    const refreshToken = localStorage.getItem('gp_refresh_token');
    const configVersion = localStorage.getItem('gp_config_version');
    const savedLang = localStorage.getItem('glowpic_lang');
    localStorage.clear();
    if (accessToken) localStorage.setItem('gp_access_token', accessToken);
    if (refreshToken) localStorage.setItem('gp_refresh_token', refreshToken);
    if (configVersion) localStorage.setItem('gp_config_version', configVersion);
    if (savedLang) localStorage.setItem('glowpic_lang', savedLang);

    setTimeout(() => {
      setCacheCleared(true);
      setCacheSize('0 B');
      setTimeout(() => {
        setCacheCleared(false);
        setActiveModal(null);
      }, 1500);
    }, 800);
  };

  const handleDeleteAccount = async () => {
    if (deleteStep === 1) { setDeleteStep(2); setDeleteConfirmText(''); setDeleteError(''); return; }
    if (deleteConfirmText !== t('settings_confirmDeletePlaceholder')) return;
    try {
      await deleteMe();
    } catch (requestError) {
      setDeleteError(requestError instanceof Error ? requestError.message : t('settings_deleteFailed'));
      return;
    }
    await clearTokens();
    logoutUser();
    setActiveModal(null);
    setDeleteStep(1);
    setDeleteConfirmText('');
    navigate('/login', { replace: true });
  };

  const settingGroups = [
    {
      title: t('settings_account'),
      items: [
        { icon: 'ri-shield-user-line', label: t('settings_accountSecurity'), color: '#FF6B9D', action: () => setActiveModal('accountSecurity') },
        { icon: 'ri-notification-3-line', label: t('settings_notifications'), color: '#A855F7', action: () => setActiveModal('notifications') },
      ],
    },
    {
      title: t('settings_general'),
      items: [
        { icon: 'ri-delete-bin-line', label: t('settings_clearCache'), color: '#6366F1', desc: cacheSize, action: () => setActiveModal('clearCache') },
        { icon: 'ri-translate-2', label: t('settings_language'), color: '#EC4899', desc: currentLang === 'zh' ? '简体中文' : 'English', action: () => setActiveModal('language') },
      ],
    },
    {
      title: t('settings_about'),
      items: [
        { icon: 'ri-file-text-line', label: t('settings_agreement'), color: '#8B5CF6', action: () => setActiveAgreement('user_agreement') },
        { icon: 'ri-shield-check-line', label: t('settings_privacy'), color: '#A855F7', action: () => setActiveAgreement('privacy_policy') },
        { icon: 'ri-information-line', label: t('settings_aboutUs'), color: '#FF6B9D', action: () => setActiveModal('about') },
        { icon: 'ri-star-line', label: t('settings_rateUs'), color: '#F59E0B', action: () => { setRateScore(0); setRateSubmitted(false); setActiveModal('rateUs'); } },
      ],
    },
  ];

  if (!authInitialized) {
    return (
      <div className="min-h-screen font-nunito flex flex-col" style={{ background: '#F8F7FF', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="flex-1 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-pink-200 border-t-pink-500 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen font-nunito flex flex-col items-center justify-center px-5"
        style={{ background: '#F8F7FF', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="w-24 h-24 rounded-3xl flex items-center justify-center mb-6"
          style={{ background: 'linear-gradient(135deg, #FF6B9D 0%, #A855F7 100%)' }}>
          <i className="ri-settings-3-line text-white text-4xl"></i>
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">{t('settings_loginTitle')}</h2>
        <p className="text-sm text-gray-400 mb-8 text-center leading-relaxed whitespace-pre-line">
          {t('settings_loginDesc')}
        </p>
        <button
          onClick={() => navigate('/login?redirect=/settings')}
          className="w-full max-w-xs py-4 rounded-2xl text-white font-bold text-base cursor-pointer whitespace-nowrap"
          style={{ background: 'linear-gradient(135deg, #FF6B9D 0%, #A855F7 100%)' }}
        >
          {t('settings_loginBtn')}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen font-nunito" style={{ background: '#F8F7FF', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <div className="max-w-md mx-auto min-h-screen flex flex-col">

        {/* 顶部导航 */}
        <div className="flex items-center gap-3 px-5 pb-4" style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top))' }}>
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center rounded-2xl bg-white cursor-pointer"
          >
            <i className="ri-arrow-left-line text-gray-700 text-lg"></i>
          </button>
          <h1 className="text-lg font-bold text-gray-800">{t('settings_title')}</h1>
        </div>

        {/* 设置分组 */}
        <div className="px-5 flex-1 space-y-4">
          {settingGroups.map((group) => (
            <div key={group.title}>
              <p className="text-xs font-semibold text-gray-400 mb-2 px-1">{group.title}</p>
              <div className="bg-white rounded-3xl overflow-hidden">
                {group.items.map((item, idx) => (
                  <button
                    key={item.label}
                    onClick={item.action}
                    className={`w-full flex items-center gap-3 px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors ${idx < group.items.length - 1 ? 'border-b border-gray-50' : ''}`}
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0"
                      style={{ background: `${item.color}15` }}>
                      <i className={`${item.icon} text-base`} style={{ color: item.color }}></i>
                    </div>
                    <span className="flex-1 text-left text-sm font-medium text-gray-800 whitespace-nowrap">{item.label}</span>
                    {'desc' in item && item.desc && (
                      <span className="text-xs text-gray-400 whitespace-nowrap">{item.desc}</span>
                    )}
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-arrow-right-s-line text-gray-300 text-base"></i>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* 退出登录 */}
          {user && (
            <button
              onClick={() => setActiveModal('logout')}
              data-testid="settings-logout"
              aria-label="settings-logout"
              className="w-full bg-white rounded-2xl py-4 text-center text-sm font-semibold text-red-400 cursor-pointer whitespace-nowrap hover:bg-red-50 transition-colors"
            >
              {t('settings_logout')}
            </button>
          )}

          <p className="text-center text-xs text-gray-300 pb-8">{t('settings_footerText')}</p>
        </div>
      </div>

      {/* 退出确认弹窗 */}
      {activeModal === 'logout' && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40">
          <div className="w-full max-w-md bg-white rounded-t-3xl p-6 animate-slide-up" style={{ paddingBottom: 'calc(2.5rem + env(safe-area-inset-bottom))' }}>
            <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-logout-box-line text-red-400 text-xl"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-800 text-center mb-2">{t('settings_logoutConfirm')}</h3>
            <p className="text-sm text-gray-400 text-center mb-6">{t('settings_logoutDesc')}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setActiveModal(null)}
                className="flex-1 h-12 rounded-2xl bg-gray-100 text-gray-600 font-semibold text-sm cursor-pointer whitespace-nowrap"
              >
                {t('settings_cancel')}
              </button>
              <button
                onClick={handleLogout}
                data-testid="settings-logout-confirm"
                aria-label="settings-logout-confirm"
                className="flex-1 h-12 rounded-2xl bg-red-400 text-white font-semibold text-sm cursor-pointer whitespace-nowrap"
              >
                {t('settings_logoutConfirmBtn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 清除缓存弹窗 */}
      {activeModal === 'clearCache' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-5 bg-black/40">
          <div className="w-full max-w-sm bg-white rounded-3xl p-6 animate-scale-in">
            {cacheCleared ? (
              <div className="flex flex-col items-center py-4">
                <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mb-3">
                  <i className="ri-check-line text-green-500 text-2xl"></i>
                </div>
                <p className="text-base font-bold text-gray-800">{t('settings_clearCacheSuccess')}</p>
                <p className="text-sm text-gray-400 mt-1">{t('settings_cacheFreedDesc', { size: freedSize })}</p>
              </div>
            ) : (
              <>
                <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-delete-bin-line text-indigo-400 text-xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-800 text-center mb-2">{t('settings_clearCacheTitle')}</h3>
                <p className="text-sm text-gray-400 text-center mb-6">{t('settings_clearCacheDesc')} {cacheSize} {t('settings_clearCacheDesc2')}</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setActiveModal(null)}
                    className="flex-1 h-12 rounded-2xl bg-gray-100 text-gray-600 font-semibold text-sm cursor-pointer whitespace-nowrap"
                  >
                    {t('settings_cancel')}
                  </button>
                  <button
                    onClick={handleClearCache}
                    className="flex-1 h-12 rounded-2xl text-white font-semibold text-sm cursor-pointer whitespace-nowrap"
                    style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
                  >
                    {t('settings_clearCacheConfirm')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 关于我们弹窗 */}
      {activeModal === 'about' && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/40">
          <div className="w-full max-w-md mx-auto bg-white rounded-t-3xl p-6 animate-slide-up" style={{ paddingBottom: 'calc(2.5rem + env(safe-area-inset-bottom))' }}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-800">{t('settings_aboutTitle')}</h3>
              <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="flex flex-col items-center mb-6">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-3 overflow-hidden bg-white"
                style={{ boxShadow: '0 4px 16px rgba(168, 85, 247, 0.12)' }}>
                <img src={APP_BRAND_MARK_URL} alt="logo" className="w-10 h-10 object-contain rounded-lg" />
              </div>
              <p className="text-lg font-bold text-gray-800">{t('appName')}</p>
              <p className="text-sm text-gray-400 mt-1">{t('settings_version')}</p>
            </div>
            <p className="text-sm text-gray-500 leading-relaxed text-center">
              {t('settings_aboutDesc')}
            </p>
            <div className="mt-5 pt-5 border-t border-gray-100 text-center">
              <p className="text-xs text-gray-300">{t('settings_copyright')}</p>
            </div>
          </div>
        </div>
      )}

      {/* 账号与安全弹窗 */}
      {activeModal === 'accountSecurity' && (
        <AccountSecuritySheet
          onClose={() => setActiveModal(null)}
          onDeleteAccount={() => setActiveModal('deleteAccount')}
        />
      )}

      {/* 注销账号弹窗 */}
      {activeModal === 'deleteAccount' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-5 bg-black/40">
          <div className="w-full max-w-sm bg-white rounded-3xl p-6 animate-scale-in">
            {deleteStep === 1 ? (
              <>
                <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-alert-line text-red-400 text-xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-800 text-center mb-2">{t('settings_deleteAccountTitle')}</h3>
                <div className="text-sm text-gray-500 leading-relaxed space-y-2 mb-6">
                  <p>{t('settings_deleteAccountDesc')}</p>
                  <ul className="space-y-1 text-gray-400">
                    <li className="flex items-start gap-2"><i className="ri-close-circle-line text-red-300 mt-0.5"></i><span>{t('settings_deleteItem1')}</span></li>
                    <li className="flex items-start gap-2"><i className="ri-close-circle-line text-red-300 mt-0.5"></i><span>{t('settings_deleteItem2')}</span></li>
                    <li className="flex items-start gap-2"><i className="ri-close-circle-line text-red-300 mt-0.5"></i><span>{t('settings_deleteItem3')}</span></li>
                  </ul>
                  <p className="text-red-400">{t('settings_deleteWarning')}</p>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setActiveModal('accountSecurity')} className="flex-1 h-12 rounded-2xl bg-gray-100 text-gray-600 font-semibold text-sm cursor-pointer whitespace-nowrap">{t('settings_cancel')}</button>
                  <button onClick={handleDeleteAccount} className="flex-1 h-12 rounded-2xl bg-red-400 text-white font-semibold text-sm cursor-pointer whitespace-nowrap">{t('settings_continueDelete')}</button>
                </div>
              </>
            ) : (
              <>
                <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-delete-bin-line text-red-400 text-xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-800 text-center mb-2">{t('settings_confirmDeleteTitle')}</h3>
                <p className="text-sm text-gray-400 text-center mb-4">{t('settings_confirmDeleteDesc')}</p>
                {deleteError && <p className="text-xs text-red-500 bg-red-50 rounded-xl p-3 mb-4">{deleteError}</p>}
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={t('settings_confirmDeletePlaceholder')}
                  className="w-full h-12 bg-gray-50 rounded-2xl px-4 text-sm text-gray-800 outline-none placeholder-gray-300 text-center mb-6"
                />
                <div className="flex gap-3">
                  <button onClick={() => { setDeleteStep(1); setDeleteConfirmText(''); }} className="flex-1 h-12 rounded-2xl bg-gray-100 text-gray-600 font-semibold text-sm cursor-pointer whitespace-nowrap">{t('settings_back')}</button>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleteConfirmText !== t('settings_confirmDeletePlaceholder')}
                    className="flex-1 h-12 rounded-2xl bg-red-400 text-white font-semibold text-sm cursor-pointer whitespace-nowrap disabled:opacity-40"
                  >{t('settings_confirmDelete')}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 消息通知弹窗 */}
      {activeModal === 'notifications' && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/40">
          <div className="w-full max-w-md mx-auto bg-white rounded-t-3xl max-h-[80vh] flex flex-col animate-slide-up">
            <div className="flex items-center justify-between px-5 pt-5 pb-4 flex-shrink-0 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-800">{t('settings_notifications')}</h3>
              <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="overflow-y-auto flex-1 px-5 py-4 space-y-1" style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))' }}>
              {([
                { key: 'generateDone' as const, labelKey: 'settings_notifyGenerateDone', descKey: 'settings_notifyGenerateDoneDesc' },
                { key: 'activity' as const, labelKey: 'settings_notifyActivity', descKey: 'settings_notifyActivityDesc' },
                { key: 'system' as const, labelKey: 'settings_notifySystem', descKey: 'settings_notifySystemDesc' },
              ]).map((item) => (
                <div key={item.key} className="flex items-center justify-between py-3.5 border-b border-gray-50">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{t(item.labelKey)}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{t(item.descKey)}</p>
                  </div>
                  <button
                    onClick={() => setNotifications((prev) => ({ ...prev, [item.key]: !prev[item.key] }))}
                    className={`w-11 h-6 rounded-full transition-colors duration-300 relative cursor-pointer ${notifications[item.key] ? 'bg-pink-400' : 'bg-gray-200'}`}
                  >
                    <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-300 ${notifications[item.key] ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 语言设置弹窗 */}
      {activeModal === 'language' && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/40">
          <div className="w-full max-w-md mx-auto bg-white rounded-t-3xl p-6 animate-slide-up" style={{ paddingBottom: 'calc(2.5rem + env(safe-area-inset-bottom))' }}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-800">{t('settings_languageTitle')}</h3>
              <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="space-y-2">
              <button
                onClick={() => handleSwitchLang('zh')}
                className="w-full flex items-center justify-between py-3 px-4 rounded-2xl cursor-pointer transition-all"
                style={{ background: currentLang === 'zh' ? '#FFF0F6' : '#F9FAFB' }}
              >
                <div className="flex items-center gap-3">
                  {currentLang === 'zh'
                    ? <i className="ri-check-line text-pink-500 text-lg"></i>
                    : <div className="w-5"></div>}
                  <span className="text-sm font-medium text-gray-800">{t('settings_langZh')}</span>
                </div>
                {currentLang === 'zh' && <span className="text-xs text-pink-500 font-medium">{t('settings_currentLang')}</span>}
              </button>
              <button
                onClick={() => handleSwitchLang('en')}
                className="w-full flex items-center justify-between py-3 px-4 rounded-2xl cursor-pointer transition-all"
                style={{ background: currentLang === 'en' ? '#FFF0F6' : '#F9FAFB' }}
              >
                <div className="flex items-center gap-3">
                  {currentLang === 'en'
                    ? <i className="ri-check-line text-pink-500 text-lg"></i>
                    : <div className="w-5"></div>}
                  <span className="text-sm font-medium text-gray-800">{t('settings_langEn')}</span>
                </div>
                {currentLang === 'en' && <span className="text-xs text-pink-500 font-medium">{t('settings_currentLang')}</span>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 给我们评分弹窗 */}
      {activeModal === 'rateUs' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-5 bg-black/40">
          <div className="w-full max-w-sm bg-white rounded-3xl p-6 animate-scale-in">
            {rateSubmitted ? (
              <div className="flex flex-col items-center py-4">
                <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mb-3">
                  <i className="ri-check-line text-green-500 text-2xl"></i>
                </div>
                <p className="text-base font-bold text-gray-800">{t('settings_rateThanks')}</p>
                <p className="text-sm text-gray-400 mt-1">{t('settings_rateThanksDesc')}</p>
              </div>
            ) : (
              <>
                <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-star-fill text-amber-400 text-xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-800 text-center mb-2">{t('settings_rateTitle')}</h3>
                <p className="text-sm text-gray-400 text-center mb-5">{t('settings_rateDesc')}</p>
                <div className="flex items-center justify-center gap-2 mb-6">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button key={star} onClick={() => setRateScore(star)} className="w-10 h-10 flex items-center justify-center cursor-pointer">
                      <i className={`ri-star-fill text-2xl transition-all ${star <= rateScore ? 'text-amber-400' : 'text-gray-200'}`}></i>
                    </button>
                  ))}
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setActiveModal(null)} className="flex-1 h-12 rounded-2xl bg-gray-100 text-gray-600 font-semibold text-sm cursor-pointer whitespace-nowrap">{t('settings_cancel')}</button>
                  <button
                    onClick={() => {
                      if (rateScore > 0) {
                        setRateSubmitted(true);
                        setTimeout(() => { setRateSubmitted(false); setActiveModal(null); }, 1500);
                      }
                    }}
                    disabled={rateScore === 0}
                    className="flex-1 h-12 rounded-2xl text-white font-semibold text-sm cursor-pointer whitespace-nowrap disabled:opacity-50"
                    style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
                  >{t('settings_rateSubmit')}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 协议弹层 */}
      {activeAgreement && config?.agreements?.[activeAgreement] && (
        <AgreementSheet
          agreement={config.agreements[activeAgreement]}
          onClose={() => setActiveAgreement(null)}
        />
      )}
    </div>
  );
}
